<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="İletişim"
      title="Bildirim merkezi"
      description="Broadcast yayınları ve akıllı push kurallarını daha okunabilir bir operasyon yüzeyiyle yönetin."
    >
      <template #actions>
        <div
          v-if="activeTab === 0 || activeTab === 1"
          class="notification-header-actions"
        >
          <v-btn
            v-if="activeTab === 0"
            color="primary"
            @click="openBroadcastDialog"
          >
            <v-icon left>
              {{ icons.mdiBellOutline }}
            </v-icon>
            Bildirim Yarat
          </v-btn>
          <v-btn
            v-else-if="activeTab === 1"
            outlined
            color="primary"
            @click="requestRuleAction('bootstrap')"
          >
            <v-icon left>
              {{ icons.mdiAutoFix }}
            </v-icon>
            Varsayılan kuralları kur
          </v-btn>
          <v-btn
            v-if="activeTab === 1"
            color="primary"
            @click="openRuleDialog()"
          >
            <v-icon left>
              {{ icons.mdiBullhorn }}
            </v-icon>
            Kural Ekle
          </v-btn>
        </div>
      </template>
    </admin-page-header>

    <v-card
      class="admin-table-card"
      outlined
      style="overflow: hidden;"
    >
      <v-alert
        v-if="broadcastListError || ruleError"
        dense
        outlined
        type="error"
        class="ma-4"
      >
        {{ broadcastListError || ruleError }}
      </v-alert>
      <v-alert
        v-if="broadcastSuccess && !broadcastDialog"
        dense
        outlined
        type="success"
        class="ma-4"
      >
        {{ broadcastSuccess }}
      </v-alert>
      <v-tabs
        v-model="activeTab"
        class="admin-tabs notification-tabs"
      >
        <v-tab>Toplu Bildirimler</v-tab>
        <v-tab>Akıllı Push Kuralları</v-tab>
        <v-tab>Panel Bildirimler</v-tab>
        <v-tab>Bütün Bildirimler</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card-text class="pa-6">
            <div class="notification-filters">
              <v-text-field
                v-model="broadcastSearch"
                label="Bildirim ara"
                dense
                outlined
                clearable
                hide-details
              />
              <v-select
                v-model="broadcastStatus"
                :items="broadcastStatusOptions"
                label="Durum"
                dense
                outlined
                hide-details
              />
              <v-btn
                icon
                title="Bildirimleri yenile"
                aria-label="Bildirimleri yenile"
                :loading="broadcastLoading"
                @click="loadBroadcasts"
              >
                <v-icon>{{ icons.mdiRefresh }}</v-icon>
              </v-btn>
            </div>
            <div class="text-caption mb-3">
              Son 200 kayıt · Kuyruk durumu teslimat onayı değildir.
            </div>
            <v-data-table
              :headers="broadcastHeaders"
              :items="filteredBroadcasts"
              :loading="broadcastLoading"
              item-key="id"
              :footer-props="tableFooterProps"
              class="admin-data-table elevation-0"
            >
              <template v-slot:item.brandName="{ item }">
                <div class="notification-broadcast__campaign">
                  {{ item.brandName || '-' }}
                </div>
              </template>
              <template v-slot:item.campaignName="{ item }">
                <div class="notification-broadcast__campaign-name">
                  {{ item.campaignName || '-' }}
                </div>
              </template>
              <template v-slot:item.bodyText="{ item }">
                <div
                  class="notification-broadcast__body"
                  :title="item.bodyText"
                >
                  {{ item.bodyText || '-' }}
                </div>
              </template>
              <template v-slot:item.date="{ item }">
                {{ formatDateTime(item.date) }}
              </template>
              <template v-slot:item.scheduledAt="{ item }">
                {{ item.scheduledAt ? formatDateTime(item.scheduledAt) : '-' }}
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip
                  small
                  :color="item.send ? 'success' : item.isScheduled ? 'warning' : 'primary'"
                  dark
                >
                  {{ item.send ? 'İşlendi' : item.isScheduled ? 'Planlandı' : 'Kuyrukta' }}
                </v-chip>
              </template>
              <template v-slot:item.image="{ item }">
                <v-avatar
                  v-if="item.image && item.image.localUrl"
                  size="36"
                >
                  <v-img
                    :src="item.image.localUrl"
                    cover
                  ></v-img>
                </v-avatar>
                <span v-else>-</span>
              </template>
              <template v-slot:no-data>
                <div class="pa-6 text-center admin-empty-state">
                  Henüz toplu bildirim kaydı yok.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>

        <v-tab-item>
          <v-card-text class="pa-6">
            <v-alert
              v-if="ruleSuccess"
              dense
              outlined
              type="success"
              class="mb-4"
            >
              {{ ruleSuccess }}
            </v-alert>

            <v-row class="mb-4">
              <v-col
                cols="12"
                md="8"
              >
                <v-text-field
                  v-model="ruleSearch"
                  dense
                  outlined
                  clearable
                  label="Kural ara"
                ></v-text-field>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-select
                  v-model="ruleStatusFilter"
                  :items="ruleStatusOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  outlined
                  label="Durum"
                ></v-select>
              </v-col>
            </v-row>

            <v-data-table
              :headers="ruleHeaders"
              :items="filteredRules"
              :loading="ruleLoading"
              item-key="id"
              :footer-props="tableFooterProps"
              class="admin-data-table elevation-0"
            >
              <template v-slot:item.triggerType="{ item }">
                {{ triggerOptions[item.triggerType] || item.triggerType }}
              </template>
              <template v-slot:item.active="{ item }">
                <v-switch
                  :input-value="item.active"
                  inset
                  dense
                  hide-details
                  :disabled="ruleActionBusy || Boolean(ruleAction)"
                  :aria-label="`${item.name}: aktiflik`"
                  @change="toggleRuleActive(item, $event)"
                ></v-switch>
              </template>
              <template v-slot:item.image="{ item }">
                <v-avatar
                  v-if="item.image && item.image.localUrl"
                  size="36"
                >
                  <v-img
                    :src="item.image.localUrl"
                    cover
                  ></v-img>
                </v-avatar>
                <span v-else>-</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <div
                  class="d-flex align-center"
                  style="gap: 8px;"
                >
                  <v-btn
                    icon
                    small
                    color="primary"
                    :disabled="ruleActionBusy || !item.active"
                    title="Kural etkisini incele ve çalıştır"
                    aria-label="Kural etkisini incele ve çalıştır"
                    @click="requestRuleAction('run', item)"
                  >
                    <v-icon>{{ icons.mdiPlay }}</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="primary"
                    :disabled="ruleActionBusy"
                    title="Kuralı düzenle"
                    aria-label="Kuralı düzenle"
                    @click="openRuleDialog(item)"
                  >
                    <v-icon>{{ icons.mdiPencil }}</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="error"
                    :disabled="ruleActionBusy"
                    title="Kuralı sil"
                    aria-label="Kuralı sil"
                    @click="requestRuleAction('delete', item)"
                  >
                    <v-icon>{{ icons.mdiDelete }}</v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-slot:no-data>
                <div class="pa-6 text-center admin-empty-state">
                  Akıllı push kuralı yok. Varsayılan seti kurarak başlayabilirsin.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>

        <v-tab-item>
          <v-card-text class="pa-6">
            <v-alert
              text
              dense
              color="info"
              class="mb-4"
            >
              Offer, premium durum değişimi, kampanya URL sağlığı ve WhatsApp destek bildirimleri burada toplanır. Bu kayıtlar yalnızca panel içindir.
            </v-alert>

            <v-alert
              v-if="payoutFeedError"
              dense
              outlined
              type="warning"
              class="mb-4"
            >
              {{ payoutFeedError }}
            </v-alert>

            <v-data-table
              :headers="panelFeedHeaders"
              :items="payoutFeed"
              :loading="payoutFeedLoading"
              item-key="id"
              :footer-props="tableFooterProps"
              class="admin-data-table elevation-0"
            >
              <template v-slot:top>
                <v-toolbar
                  flat
                  dense
                  class="admin-page-toolbar"
                >
                  <v-toolbar-title>Panel event feed'i</v-toolbar-title>
                  <v-spacer></v-spacer>
                  <v-chip
                    small
                    class="mr-2"
                  >
                    {{ payoutFeed.length }} kayıt
                  </v-chip>
                  <v-chip
                    small
                    color="primary"
                    outlined
                  >
                    {{ payoutFeedStats.deltaCount }} payout değişimi
                  </v-chip>
                </v-toolbar>
              </template>

              <template v-slot:item.offerName="{ item }">
                <div class="notification-feed__title">
                  {{ item.premiumId ? (item.userName || item.userEmail || `Kullanıcı #${item.userId}`) : (item.offerName || item.title || '-') }}
                </div>
                <div
                  v-if="item.premiumId"
                  class="text-caption"
                >
                  #{{ item.userId }} · {{ item.userEmail || '-' }} · Premium #{{ item.premiumId }}
                </div>
              </template>

              <template v-slot:item.type="{ item }">
                <v-chip
                  small
                  outlined
                  :color="panelEventColor(item)"
                >
                  <v-icon
                    left
                    small
                  >
                    {{ panelEventIcon(item) }}
                  </v-icon>
                  {{ panelEventLabel(item.type) }}
                </v-chip>
              </template>

              <template v-slot:item.summary="{ item }">
                <div
                  class="notification-feed__body"
                  :title="buildPanelEventSubtitle(item)"
                >
                  {{ buildPanelEventSubtitle(item) }}
                </div>
              </template>

              <template v-slot:item.createdAt="{ item }">
                {{ formatDateTime(item.createdAt || item.created || item.date || item.modified) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  v-if="String(item.type || '').toLowerCase() === 'admin-campaign-url-health' && item.campaignId"
                  small
                  text
                  color="primary"
                  @click="openCampaignUrlHealth(item)"
                >
                  İncele
                  <v-icon
                    right
                    small
                  >
                    {{ icons.mdiOpenInNew }}
                  </v-icon>
                </v-btn>
                <v-btn
                  v-else-if="item.premiumId"
                  small
                  text
                  color="primary"
                  to="/premium"
                >
                  Premium
                </v-btn>
                <span v-else>-</span>
              </template>

              <template v-slot:no-data>
                <div class="pa-6 text-center admin-empty-state">
                  Henüz panel bildirimi yok.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>

        <v-tab-item>
          <v-card-text class="pa-6">
            <div class="notification-all-toolbar mb-4">
              <v-select
                v-model="allNotificationFilters.type"
                :items="allNotificationTypeOptions"
                label="Bildirim tipi"
                outlined
                dense
                hide-details
                @change="applyAllNotificationFilters"
              ></v-select>
              <v-text-field
                v-model="allNotificationFilters.user"
                label="Kullanıcı ara"
                placeholder="User ID, ad soyad veya e-posta"
                :prepend-inner-icon="icons.mdiMagnify"
                clearable
                outlined
                dense
                hide-details
                @input="scheduleAllNotificationFilter"
              ></v-text-field>
              <v-chip
                outlined
                color="primary"
                class="notification-all-toolbar__count"
              >
                {{ allNotificationsTotal }} kayıt
              </v-chip>
            </div>

            <v-alert
              v-if="allNotificationsError"
              dense
              outlined
              type="warning"
              class="mb-4"
            >
              {{ allNotificationsError }}
            </v-alert>

            <v-data-table
              :headers="allNotificationHeaders"
              :items="allNotifications"
              :loading="allNotificationsLoading"
              :options.sync="allNotificationOptions"
              :server-items-length="allNotificationsTotal"
              disable-sort
              item-key="id"
              :footer-props="tableFooterProps"
              class="admin-data-table elevation-0"
            >
              <template v-slot:item.userLabel="{ item }">
                <div class="notification-feed__user">
                  {{ item.userLabel || '-' }}
                </div>
              </template>
              <template v-slot:item.title="{ item }">
                <div class="notification-feed__title">
                  {{ item.offerName || item.title || '-' }}
                </div>
              </template>
              <template v-slot:item.type="{ item }">
                <v-chip
                  small
                  outlined
                  :color="panelEventColor(item)"
                >
                  <v-icon
                    left
                    small
                  >
                    {{ panelEventIcon(item) }}
                  </v-icon>
                  {{ panelEventLabel(item.type) }}
                </v-chip>
              </template>
              <template v-slot:item.message="{ item }">
                <div
                  class="notification-feed__body"
                  :title="item.message"
                >
                  {{ item.message || '-' }}
                </div>
              </template>
              <template v-slot:item.createdAt="{ item }">
                {{ formatDateTime(item.createdAt || item.created || item.date || item.modified) }}
              </template>
              <template v-slot:no-data>
                <div class="pa-6 text-center admin-empty-state">
                  Henüz genel bildirim kaydı yok.
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>

      <v-dialog
        v-model="broadcastDialog"
        max-width="1080px"
        content-class="notification-dialog"
        persistent
      >
        <v-card class="admin-dialog-card">
          <v-toolbar
            dense
            :elevation="0"
          >
            <v-toolbar-title>Bildirim oluştur</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              :disabled="broadcastSaving || testSaving"
              aria-label="Bildirim formunu kapat"
              title="Bildirim formunu kapat"
              @click="closeBroadcastDialog"
            >
              <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pa-6 notification-composer">
            <section class="notification-composer__form">
              <v-alert
                v-if="broadcastError"
                dense
                outlined
                type="error"
                class="mb-4"
              >
                {{ broadcastError }}
              </v-alert>
              <v-alert
                v-if="broadcastSuccess"
                dense
                outlined
                type="success"
                class="mb-4"
              >
                {{ broadcastSuccess }}
              </v-alert>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="broadcastForm.title"
                      outlined
                      dense
                      label="Başlık"
                      :disabled="broadcastSaving || testSaving"
                      :rules="requiredRules"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-select
                      v-model="destination"
                      :items="destinationOptions"
                      outlined
                      dense
                      label="Açılacak içerik"
                      :disabled="broadcastSaving || testSaving"
                      @change="changeDestination"
                    ></v-select>
                  </v-col>
                  <v-col
                    v-if="destination !== 'GENERAL'"
                    cols="12"
                    md="6"
                  >
                    <v-autocomplete
                      v-model="broadcastForm.secretData"
                      :items="destinations"
                      item-text="name"
                      item-value="id"
                      label="Kampanya / Marka"
                      outlined
                      dense
                      :loading="destinationsLoading"
                      :disabled="broadcastSaving || testSaving"
                      :search-input.sync="destinationSearch"
                      no-filter
                      hide-no-data
                    />
                    <v-alert
                      v-if="destinationError"
                      dense
                      type="error"
                      outlined
                    >
                      {{ destinationError }}
                    </v-alert>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="broadcastForm.bodyText"
                      outlined
                      dense
                      rows="4"
                      label="Bildirim metni"
                      :disabled="broadcastSaving || testSaving"
                      :rules="requiredRules"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-autocomplete
                      v-model="broadcastForm.imageId"
                      :items="activeImages"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      clearable
                      label="Görsel"
                      :disabled="broadcastSaving || testSaving"
                    ></v-autocomplete>
                    <div class="d-flex justify-end mt-2">
                      <v-btn
                        small
                        outlined
                        color="primary"
                        :loading="imageUploadLoading === 'broadcast'"
                        :disabled="broadcastSaving || testSaving"
                        @click="uploadNotificationImage('broadcast')"
                      >
                        Görsel Yükle
                      </v-btn>
                    </div>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                  >
                    <v-radio-group
                      v-model="sendMode"
                      row
                      :disabled="broadcastSaving || testSaving"
                    >
                      <v-radio
                        value="NOW"
                        label="Şimdi"
                      /><v-radio
                        value="SCHEDULED"
                        label="Zamanla"
                      />
                    </v-radio-group>
                    <v-text-field
                      v-if="sendMode === 'SCHEDULED'"
                      v-model="broadcastForm.scheduledAt"
                      outlined
                      dense
                      type="datetime-local"
                      label="Planlanan tarih (Türkiye saati)"
                      :disabled="broadcastSaving || testSaving"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </section>
            <aside class="notification-composer__preview">
              <h3 class="text-subtitle-1 mb-3">
                Önizleme
              </h3>
              <div class="notification-device-preview">
                <div class="text-caption mb-2">
                  Adverport · şimdi
                </div>
                <strong>{{ broadcastForm.title || 'Bildirim başlığı' }}</strong>
                <p>{{ broadcastForm.bodyText || 'Bildirim metni' }}</p>
                <v-img
                  v-if="selectedBroadcastImage"
                  :src="selectedBroadcastImage"
                  max-height="150"
                  contain
                />
              </div>
              <h3 class="text-subtitle-1 mt-6">
                Hedef kitle
              </h3>
              <v-progress-linear
                v-if="audienceLoading"
                indeterminate
                class="my-3"
              />
              <v-alert
                v-if="audienceError"
                type="error"
                dense
                outlined
              >
                {{ audienceError }}<v-btn
                  text
                  small
                  @click="loadAudience"
                >
                  Yeniden dene
                </v-btn>
              </v-alert>
              <dl
                v-if="audience"
                class="notification-audience"
              >
                <dt>İzinli aktif kullanıcı</dt><dd>{{ audience.eligibleAudience }}</dd>
                <dt>Push kaydı olan</dt><dd>{{ audience.usersWithPushTarget }}</dd>
                <dt>Politika gereği hariç</dt><dd>{{ audience.excludedByRecipientPolicy }}</dd>
              </dl>
              <p class="text-caption">
                Gönderim anında alıcı tercihleri ve cihaz durumu yeniden değerlendirilir. Bu sayılar teslimat garantisi değildir.
              </p>
              <div class="notification-test-recipient">
                <v-text-field
                  v-model="testUserId"
                  type="number"
                  label="Test alıcısı kullanıcı no"
                  outlined
                  dense
                  hide-details
                  :disabled="testSaving || broadcastSaving"
                />
                <v-btn
                  outlined
                  :loading="testSaving"
                  :disabled="broadcastSaving || !testUserId"
                  @click="sendTest"
                >
                  Test gönder
                </v-btn>
              </div>
              <v-alert
                v-if="testResult"
                type="success"
                dense
                outlined
                class="mt-3"
              >
                {{ testResult }}
              </v-alert>
              <v-checkbox
                v-model="broadcastConfirmed"
                :disabled="broadcastSaving || testSaving || !audience"
                label="İçeriği, hedefi ve gönderim zamanını kontrol ettim."
              />
            </aside>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              :disabled="broadcastSaving || testSaving"
              @click="closeBroadcastDialog"
            >
              İptal
            </v-btn>
            <v-btn
              color="primary"
              :loading="broadcastSaving"
              :disabled="broadcastSaving || testSaving || !broadcastConfirmed || !audience || audience.eligibleAudience === 0"
              @click="saveBroadcast"
            >
              {{ sendMode === 'SCHEDULED' ? 'Bildirimi zamanla' : 'Gönderim kuyruğuna al' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        :value="Boolean(ruleAction)"
        max-width="560px"
        persistent
      >
        <v-card
          v-if="ruleAction"
          class="admin-dialog-card"
        >
          <v-card-title>{{ ruleAction.kind === 'run' ? 'Kuralı çalıştır' : ruleAction.kind === 'delete' ? 'Kuralı sil' : 'Varsayılan kuralları kur' }}</v-card-title>
          <v-card-text>
            <strong>{{ ruleAction.item.name || 'Varsayılan akıllı bildirim kuralları' }}</strong>
            <v-progress-linear
              v-if="rulePreviewLoading"
              indeterminate
              class="my-4"
            />
            <p
              v-if="ruleAction.kind === 'run' && rulePreview"
              class="mt-4"
            >
              Şu an uygun {{ rulePreview.eligibleNotifications }} bildirim var. Gönderim anındaki kontroller nedeniyle sonuç değişebilir. Bildirimler kuyruğa alınır.
            </p>
            <p
              v-else-if="ruleAction.kind === 'delete'"
              class="mt-4"
            >
              Bu kural silinecek. Önceden kuyruğa alınan bildirimler iptal edilmez.
            </p>
            <p
              v-else-if="ruleAction.kind === 'bootstrap'"
              class="mt-4"
            >
              Eksik varsayılan kurallar aktif olarak oluşturulur ve zamanlanmış bildirim işlemleri tarafından çalıştırılabilir.
            </p>
            <v-alert
              v-if="ruleActionError"
              type="error"
              dense
              outlined
            >
              {{ ruleActionError }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer /><v-btn
              text
              :disabled="ruleActionBusy"
              @click="ruleAction = null"
            >
              Vazgeç
            </v-btn><v-btn
              color="error"
              :loading="ruleActionBusy"
              :disabled="rulePreviewLoading || (ruleAction.kind === 'run' && !rulePreview)"
              @click="confirmRuleAction"
            >
              Onayla
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="ruleDialog"
        max-width="860px"
      >
        <v-card class="admin-dialog-card">
          <v-toolbar
            dense
            :elevation="0"
          >
            <v-toolbar-title>{{ editingRuleId ? 'Akıllı Kural Düzenle' : 'Akıllı Kural Oluştur' }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              icon
              @click="closeRuleDialog"
            >
              <v-icon>{{ icons.mdiClose }}</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pa-6">
            <v-alert
              v-if="ruleError"
              dense
              outlined
              type="error"
              class="mb-4"
            >
              {{ ruleError }}
            </v-alert>
            <v-alert
              v-if="ruleSuccess"
              dense
              outlined
              type="success"
              class="mb-4"
            >
              {{ ruleSuccess }}
            </v-alert>
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="ruleForm.name"
                    outlined
                    dense
                    label="Kural Adı"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="ruleForm.code"
                    outlined
                    dense
                    label="Code"
                    placeholder="LINK_IDLE_SHARE_REMINDER"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-select
                    v-model="ruleForm.triggerType"
                    :items="Object.keys(triggerOptions).map(key => ({ value: key, text: triggerOptions[key] }))"
                    item-text="text"
                    item-value="value"
                    outlined
                    dense
                    label="Tetik Tipi"
                    :rules="requiredRules"
                  ></v-select>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-autocomplete
                    v-model="ruleForm.imageId"
                    :items="activeImages"
                    item-text="name"
                    item-value="id"
                    outlined
                    dense
                    clearable
                    label="Görsel"
                  ></v-autocomplete>
                  <div class="d-flex justify-end mt-2">
                    <v-btn
                      small
                      outlined
                      color="primary"
                      :loading="imageUploadLoading === 'rule'"
                      @click="uploadNotificationImage('rule')"
                    >
                      Görsel Yükle
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="ruleForm.description"
                    outlined
                    dense
                    rows="2"
                    label="Açıklama"
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="ruleForm.titleTemplate"
                    outlined
                    dense
                    label="Başlık Şablonu"
                    :rules="requiredRules"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="ruleForm.messageTemplate"
                    outlined
                    dense
                    rows="3"
                    label="Mesaj Şablonu"
                    :rules="requiredRules"
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="ruleForm.delayMinutes"
                    outlined
                    dense
                    type="number"
                    label="Delay (dk)"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="ruleForm.cooldownMinutes"
                    outlined
                    dense
                    type="number"
                    label="Cooldown (dk)"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="ruleForm.dedupWindowHours"
                    outlined
                    dense
                    type="number"
                    label="Dedup Window (saat)"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model.number="ruleForm.peakHour"
                    outlined
                    dense
                    type="number"
                    min="0"
                    max="23"
                    label="Peak Hour"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model="ruleForm.quietStart"
                    outlined
                    dense
                    type="time"
                    label="Sessiz Saat Başlangıç"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-text-field
                    v-model="ruleForm.quietEnd"
                    outlined
                    dense
                    type="time"
                    label="Sessiz Saat Bitiş"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="ruleForm.type"
                    outlined
                    dense
                    label="Notification Type"
                    placeholder="Smart Push"
                  ></v-text-field>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="ruleForm.secretDataTemplate"
                    outlined
                    dense
                    label="Secret Data Template"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-switch
                    v-model="ruleForm.active"
                    label="Aktif"
                  ></v-switch>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              @click="closeRuleDialog"
            >
              İptal
            </v-btn>
            <v-btn
              color="primary"
              :loading="ruleSaving"
              @click="saveRule"
            >
              Kaydet
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios'
import * as icons from '@mdi/js'
import { getAccessToken } from '@/utils/panelSession'
import { resolveApiErrorMessage } from '@/utils/apiErrors'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'

const defaultBroadcastForm = () => ({
  title: '',
  bodyText: '',
  type: 'Broadcast',
  imageId: null,
  secretData: '',
  scheduledAt: '',
})

const defaultRuleForm = () => ({
  code: '',
  name: '',
  description: '',
  triggerType: 'LINK_IDLE_SHARE_REMINDER',
  titleTemplate: '',
  messageTemplate: '',
  type: 'Smart Push',
  secretDataTemplate: '',
  active: true,
  delayMinutes: 120,
  cooldownMinutes: 120,
  dedupWindowHours: 24,
  peakHour: 19,
  quietStart: '23:00',
  quietEnd: '09:00',
  imageId: null,
})

export default {
  components: {
    AdminPageHeader,
  },
  data: () => ({
    icons,
    activeTab: 0,
    broadcastDialog: false,
    broadcastLoading: false,
    broadcastSaving: false,
    broadcastError: '',
    broadcastSuccess: '',
    broadcastListError: '',
    broadcastSearch: '',
    broadcastStatus: 'ALL',
    broadcastStatusOptions: [{ text: 'Tümü', value: 'ALL' }, { text: 'Kuyrukta / Planlandı', value: 'PENDING' }, { text: 'İşlendi', value: 'SENT' }],
    broadcastConfirmed: false,
    sendMode: 'NOW',
    destination: 'GENERAL',
    destinationOptions: [{ text: 'Genel bildirim', value: 'GENERAL' }, { text: 'Kampanya / Koleksiyon', value: 'CAMPAIGN' }, { text: 'Marka', value: 'BRAND' }],
    destinations: [],
    destinationSearch: '',
    destinationError: '',
    destinationsLoading: false,
    destinationTimer: null,
    destinationRequest: 0,
    audience: null,
    audienceError: '',
    audienceLoading: false,
    testUserId: '',
    testSaving: false,
    testResult: '',
    ruleAction: null,
    rulePreview: null,
    rulePreviewLoading: false,
    ruleActionBusy: false,
    ruleActionError: '',
    ruleError: '',
    ruleSuccess: '',
    ruleDialog: false,
    ruleLoading: false,
    ruleSaving: false,
    imageUploadLoading: '',
    editingRuleId: null,
    broadcasts: [],
    rules: [],
    ruleSearch: '',
    ruleStatusFilter: 'ALL',
    payoutFeed: [],
    payoutFeedLoading: false,
    payoutFeedError: '',
    allNotifications: [],
    allNotificationsLoading: false,
    allNotificationsError: '',
    allNotificationsTotal: 0,
    allNotificationOptions: {
      page: 1,
      itemsPerPage: 20,
      sortBy: [],
      sortDesc: [],
    },
    allNotificationFilters: {
      type: null,
      user: '',
    },
    allNotificationTypeOptions: [
      { text: 'Tümü', value: null },
    ],
    allNotificationFilterTimer: null,
    allNotificationRequestId: 0,
    activeImages: [],
    broadcastForm: defaultBroadcastForm(),
    ruleForm: defaultRuleForm(),
    requiredRules: [v => !!(v || v === 0) || 'Zorunlu alan'],
    triggerOptions: {
      LINK_IDLE_SHARE_REMINDER: 'Link 2 saattir bekliyor',
      PEAK_TIME_SHARE_REMINDER: 'Peak time',
      FIRST_CLICK_CELEBRATION: 'İlk tıklama geldi',
    },
    ruleStatusOptions: [
      { text: 'Tümü', value: 'ALL' },
      { text: 'Aktif', value: 'ACTIVE' },
      { text: 'Pasif', value: 'PASSIVE' },
    ],
    broadcastHeaders: [
      { text: 'Tarih', value: 'date', width: 180 },
      { text: 'Marka Adı', value: 'brandName', width: 220 },
      { text: 'Kampanya Adı', value: 'campaignName', width: 220 },
      { text: 'Tip', value: 'type', width: 180 },
      { text: 'Mesaj', value: 'bodyText', sortable: false },
      { text: 'Planlanan', value: 'scheduledAt', width: 180 },
      {
        text: 'Görsel', value: 'image', sortable: false, width: 90,
      },
      {
        text: 'Durum', value: 'status', sortable: false, width: 120,
      },
    ],
    ruleHeaders: [
      { text: 'Ad', value: 'name' },
      { text: 'Tetikleyici', value: 'triggerType', width: 220 },
      {
        text: 'Görsel', value: 'image', sortable: false, width: 90,
      },
      { text: 'Aktif', value: 'active', width: 120 },
      {
        text: 'İşlemler', value: 'actions', sortable: false, width: 160,
      },
    ],
    panelFeedHeaders: [
      { text: 'Tarih', value: 'createdAt', width: 190 },
      { text: 'İlgili kayıt / Kullanıcı', value: 'offerName', width: 300 },
      {
        text: 'Tip', value: 'type', width: 220, sortable: false,
      },
      { text: 'Mesaj', value: 'summary', sortable: false },
      {
        text: 'İşlem', value: 'actions', sortable: false, width: 110,
      },
    ],
    allNotificationHeaders: [
      { text: 'Tarih', value: 'createdAt', width: 180 },
      { text: 'User ID', value: 'userId', width: 120 },
      {
        text: 'Kullanıcı', value: 'userLabel', width: 260, sortable: false,
      },
      { text: 'Başlık', value: 'title', width: 240 },
      {
        text: 'Tip', value: 'type', width: 220, sortable: false,
      },
      { text: 'Mesaj', value: 'message', sortable: false },
    ],
  }),
  computed: {
    filteredBroadcasts() {
      const query = String(this.broadcastSearch || '').toLocaleLowerCase('tr-TR').trim()

      return this.broadcasts.filter(item => (this.broadcastStatus === 'ALL' || (this.broadcastStatus === 'SENT' ? item.send : !item.send))
        && (!query || [item.brandName, item.bodyText, item.type].join(' ').toLocaleLowerCase('tr-TR').includes(query)))
    },
    selectedBroadcastImage() { return this.activeImages.find(item => item.id === this.broadcastForm.imageId)?.localUrl || '' },
    requestOptions() {
      const user = getAccessToken()

      return {
        headers: { Authorization: `Bearer ${user}` },
      }
    },
    tableFooterProps() {
      return {
        'items-per-page-options': [10, 20, 50, 100],
      }
    },
    payoutFeedStats() {
      return {
        deltaCount: this.payoutFeed.filter(item => {
          const oldValue = Number(item.oldPayout)
          const newValue = Number(item.newPayout)

          if (Number.isNaN(oldValue) || Number.isNaN(newValue)) return false

          return oldValue !== newValue
        }).length,
      }
    },
    filteredRules() {
      return this.rules.filter(item => {
        const matchesStatus = this.ruleStatusFilter === 'ALL'
          || (this.ruleStatusFilter === 'ACTIVE' && item.active)
          || (this.ruleStatusFilter === 'PASSIVE' && !item.active)

        if (!matchesStatus) return false

        const search = (this.ruleSearch || '').trim().toLocaleLowerCase('tr-TR')
        if (!search) return true

        return [
          item.name,
          item.code,
          this.triggerOptions[item.triggerType] || item.triggerType,
        ].some(value => String(value || '').toLocaleLowerCase('tr-TR').includes(search))
      })
    },
  },
  watch: {
    broadcastForm: { deep: true, handler() { this.broadcastConfirmed = false } },
    sendMode() { this.broadcastConfirmed = false },
    destinationSearch() { clearTimeout(this.destinationTimer); this.destinationTimer = setTimeout(() => this.loadDestinations(), 300) },
    allNotificationOptions: {
      deep: true,
      handler() {
        this.loadAllNotifications()
      },
    },
  },
  mounted() {
    this.loadAll()
  },
  beforeDestroy() {
    clearTimeout(this.destinationTimer)
    if (this.allNotificationFilterTimer) {
      clearTimeout(this.allNotificationFilterTimer)
    }
  },
  methods: {
    async loadAudience() {
      this.audience = null
      this.audienceLoading = true
      this.audienceError = ''
      this.broadcastConfirmed = false
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/notifications/broadcasts/audience-preview`, this.requestOptions)
        const value = response.data?.data
        if (!Number.isInteger(value?.eligibleAudience)) throw new Error('Invalid audience response')
        this.audience = value
      } catch (error) { this.audienceError = resolveApiErrorMessage(error, 'Hedef kitle doğrulanamadı.') } finally { this.audienceLoading = false }
    },
    changeDestination() {
      this.broadcastForm.secretData = ''
      this.broadcastForm.type = { GENERAL: 'Broadcast', CAMPAIGN: 'campaign_new', BRAND: 'brand_new' }[this.destination]
      this.destinations = []
      this.destinationSearch = ''
      this.loadDestinations()
    },
    async loadDestinations() {
      this.destinationRequest += 1
      const request = this.destinationRequest
      this.destinationError = ''
      if (this.destination === 'GENERAL') {
        this.destinationsLoading = false

        return
      }
      this.destinationsLoading = true
      try {
        const campaign = this.destination === 'CAMPAIGN'
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}${campaign ? 'campaign/listForAdminV2' : 'panel/offers'}`, {
          ...this.requestOptions,
          params: {
            page: 0, size: 50, search: this.destinationSearch || undefined, ...(campaign ? { statusList: 'ACTIVE' } : { active: 1 }),
          },
        })
        if (request !== this.destinationRequest) return
        const payload = response.data?.data
        const list = Array.isArray(payload) ? payload : payload?.content
        if (!Array.isArray(list)) throw new Error('Invalid destination list')
        this.destinations = list.map(item => ({ ...item, id: String(campaign ? item.id : item.offerRefId || item.id) }))
      } catch (error) { if (request === this.destinationRequest) this.destinationError = resolveApiErrorMessage(error, 'Hedef içerikler yüklenemedi.') } finally { if (request === this.destinationRequest) this.destinationsLoading = false }
    },
    validateBroadcast(test = false) {
      this.broadcastError = ''
      if (!this.broadcastForm.title.trim() || !this.broadcastForm.bodyText.trim()) this.broadcastError = 'Başlık ve bildirim metni zorunlu.'
      else if (this.destination !== 'GENERAL' && !this.broadcastForm.secretData) this.broadcastError = 'Açılacak kampanya veya markayı seçin.'
      else if (!test && this.sendMode === 'SCHEDULED' && (!this.broadcastForm.scheduledAt || new Date(`${this.broadcastForm.scheduledAt}:00+03:00`).getTime() <= Date.now() || !Number.isFinite(new Date(`${this.broadcastForm.scheduledAt}:00+03:00`).getTime()))) this.broadcastError = 'Türkiye saatine göre gelecekte bir tarih seçin.'

      return !this.broadcastError
    },
    async sendTest() {
      if (this.testSaving || this.broadcastSaving || !this.validateBroadcast(true)) return
      const id = Number(this.testUserId)
      if (!Number.isSafeInteger(id) || id <= 0) {
        this.broadcastError = 'Geçerli bir test alıcısı kullanıcı no girin.'

        return
      }
      this.testSaving = true
      this.testResult = ''
      try {
        await axios.post(`${process.env.VUE_APP_BASE_API_URL}admin/notifications/broadcasts/test`, { ...this.broadcastForm, testUserId: id, scheduledAt: '' }, this.requestOptions)
        this.testResult = `Test yalnız #${id} için kuyruğa alındı. Teslimat henüz doğrulanmadı.`
      } catch (error) { this.broadcastError = resolveApiErrorMessage(error, 'Test bildirimi kaydedilemedi.') } finally { this.testSaving = false }
    },
    async requestRuleAction(kind, item = {}) {
      if (this.ruleActionBusy || this.rulePreviewLoading) return
      this.ruleAction = { kind, item }
      this.ruleActionError = ''
      this.rulePreview = null
      if (kind !== 'run') return
      this.rulePreviewLoading = true
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/notifications/smart-rules/${item.id}/preview`, this.requestOptions)
        if (this.ruleAction?.item.id !== item.id) return
        if (!Number.isInteger(response.data?.data?.eligibleNotifications)) throw new Error('Invalid rule preview')
        this.rulePreview = response.data.data
      } catch (error) { this.ruleActionError = resolveApiErrorMessage(error, 'Kural etkisi doğrulanamadı.') } finally { this.rulePreviewLoading = false }
    },
    async confirmRuleAction() {
      if (this.ruleActionBusy || this.rulePreviewLoading || !this.ruleAction) return
      const { kind, item } = this.ruleAction
      if (kind === 'run' && !this.rulePreview) return
      this.ruleActionBusy = true
      this.ruleActionError = ''
      try {
        const base = `${process.env.VUE_APP_BASE_API_URL}admin/notifications/smart-rules`
        if (kind === 'delete') {
          await axios.delete(`${base}/${item.id}`, this.requestOptions)
          this.ruleSuccess = `${item.name} silindi.`
        } else if (kind === 'run') {
          const response = await axios.post(`${base}/${item.id}/run`, {}, this.requestOptions)
          this.ruleSuccess = `${item.name}: ${response.data.data} bildirim kuyruğa alındı. Teslimat henüz doğrulanmadı.`
        } else if (kind === 'bootstrap') {
          const response = await axios.post(`${base}/bootstrap-defaults`, {}, this.requestOptions)
          this.ruleSuccess = `${response.data.data.created} kural oluşturuldu, ${response.data.data.skipped} mevcut kural korundu.`
        } else return
        this.ruleAction = null
        await this.loadRules()
      } catch (error) { this.ruleActionError = resolveApiErrorMessage(error, 'Kural işlemi tamamlanamadı.') } finally { this.ruleActionBusy = false }
    },
    async loadAll() {
      await Promise.all([
        this.loadBroadcasts(),
        this.loadRules(),
        this.loadPanelFeed(),
        this.loadAllNotificationTypes(),
        this.loadAllNotifications(),
        this.loadActiveImages(),
      ])
    },
    normalizeList(payload) {
      if (Array.isArray(payload)) return payload
      if (Array.isArray(payload?.list)) return payload.list
      if (Array.isArray(payload?.content)) return payload.content
      if (Array.isArray(payload?.items)) return payload.items

      return []
    },
    formatDateTime(value) {
      if (!value) return '-'
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toLocaleString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      }

      if (typeof value === 'number' && Number.isFinite(value)) {
        const parsed = new Date(value)

        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        }
      }

      const raw = String(value).trim()
      if (/^\d{10,13}$/.test(raw)) {
        const parsed = new Date(Number(raw))

        if (!Number.isNaN(parsed.getTime())) {
          return parsed.toLocaleString('tr-TR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        }
      }

      const normalized = raw.match(/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/)
        ? raw.replace(
          /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/,
          '$3-$2-$1T$4:$5:$6',
        )
        : raw.replace(' ', 'T')
      const parsed = new Date(normalized)
      if (Number.isNaN(parsed.getTime())) return value

      return parsed.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
    async loadBroadcasts() {
      this.broadcastLoading = true
      this.broadcastListError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/notifications/broadcasts`,
          this.requestOptions,
        )
        this.broadcasts = (response?.data?.data || []).map(item => ({
          ...item,
          brandName: item.brandName || item.title || '-',
          campaignName: this.resolveBroadcastCampaignName(item),
          bodyText: item.message || item.body || item.description || '-',
        }))
      } catch (error) {
        this.broadcastListError = resolveApiErrorMessage(error, 'Bildirimler yüklenemedi.')
        console.error('Broadcast notifications yüklenirken hata oluştu:', error)
      } finally {
        this.broadcastLoading = false
      }
    },
    async loadRules() {
      this.ruleLoading = true
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/notifications/smart-rules`,
          this.requestOptions,
        )
        this.rules = response?.data?.data || []
      } catch (error) {
        this.ruleError = 'Akıllı push kuralları yüklenemedi.'
        console.error('Smart rules yüklenirken hata oluştu:', error)
      } finally {
        this.ruleLoading = false
      }
    },
    async loadPanelFeed() {
      this.payoutFeedLoading = true
      this.payoutFeedError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/notifications/panel-feed`,
          this.requestOptions,
        )
        const payload = response?.data?.data ?? response?.data
        this.payoutFeed = this.normalizeList(payload).map(item => ({
          ...item,
          oldPayout: item.oldPayout ?? item.beforePayout ?? item.previousPayout ?? null,
          newPayout: item.newPayout ?? item.afterPayout ?? item.currentPayout ?? null,
          secretData: item.secretData ?? item.secret ?? '',
          offerName: item.offerName || item.title || (typeof item.message === 'string' ? item.message.slice(0, 120) : '') || '',
        }))
      } catch (error) {
        this.payoutFeedError = 'Panel bildirimleri yüklenemedi. Beklenen endpoint: GET /admin/notifications/panel-feed.'
        console.error('Panel bildirimleri yüklenirken hata oluştu:', error)
      } finally {
        this.payoutFeedLoading = false
      }
    },
    loadPayoutFeed() {
      return this.loadPanelFeed()
    },
    async loadAllNotifications() {
      const requestId = this.allNotificationRequestId + 1
      this.allNotificationRequestId = requestId
      this.allNotificationsLoading = true
      this.allNotificationsError = ''
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/notifications`,
          {
            ...this.requestOptions,
            params: {
              page: Math.max(0, Number(this.allNotificationOptions.page || 1) - 1),
              size: Number(this.allNotificationOptions.itemsPerPage || 20),
              type: this.allNotificationFilters.type || undefined,
              user: String(this.allNotificationFilters.user || '').trim() || undefined,
            },
          },
        )
        if (requestId !== this.allNotificationRequestId) return
        const payload = response?.data?.data ?? response?.data
        this.allNotifications = this.normalizeList(payload)
          .filter(item => !this.isPanelNotification(item))
          .map(item => ({
            ...item,
            offerName: item.offerName || item.title || '',
            message: item.message || '',
            userLabel: this.resolveNotificationUser(item),
          }))
        this.allNotificationsTotal = Number(payload?.totalElements ?? this.allNotifications.length)
      } catch (error) {
        if (requestId !== this.allNotificationRequestId) return
        this.allNotificationsError = 'Bütün bildirimler yüklenemedi.'
        this.allNotifications = []
        this.allNotificationsTotal = 0
        console.error('Bütün bildirimler yüklenirken hata oluştu:', error)
      } finally {
        if (requestId === this.allNotificationRequestId) {
          this.allNotificationsLoading = false
        }
      }
    },
    async loadAllNotificationTypes() {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/notifications/types`,
          this.requestOptions,
        )
        const payload = response?.data?.data ?? response?.data
        const types = this.normalizeList(payload)
          .map(type => String(type || '').trim())
          .filter(Boolean)

        this.allNotificationTypeOptions = [
          { text: 'Tümü', value: null },
          ...types.map(type => ({ text: this.panelEventLabel(type), value: type })),
        ]
      } catch (error) {
        console.error('Bildirim tipleri yüklenirken hata oluştu:', error)
      }
    },
    scheduleAllNotificationFilter() {
      if (this.allNotificationFilterTimer) {
        clearTimeout(this.allNotificationFilterTimer)
      }
      this.allNotificationFilterTimer = setTimeout(() => {
        this.applyAllNotificationFilters()
      }, 350)
    },
    applyAllNotificationFilters() {
      if (this.allNotificationOptions.page !== 1) {
        this.allNotificationOptions = {
          ...this.allNotificationOptions,
          page: 1,
        }

        return
      }
      this.loadAllNotifications()
    },
    resolveNotificationUser(item) {
      if (item?.allUsers) {
        const count = Number(item?.recipientCount || 0)

        return count > 0 ? `Tüm kullanıcılar (${count})` : 'Tüm kullanıcılar'
      }

      const name = String(item?.userName || '').trim()
      const email = String(item?.userEmail || '').trim()

      if (name && email && name !== email) {
        return `${name} (${email})`
      }

      return name || email || '-'
    },
    isPanelNotification(item) {
      return String(item?.type || '').trim().toLowerCase().startsWith('admin-')
    },
    async loadActiveImages() {
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL}images/get-all-active`,
          {
            searchValue: '',
            page: 0,
            size: 500,
            sortBy: 'created',
            sortDirection: 'ASC',
          },
          this.requestOptions,
        )
        const payload = response?.data?.data
        const list = payload?.list || payload?.content || payload || []
        this.activeImages = list.map(img => ({
          ...img,
          name: img.name || img.localUrl || `Gorsel #${img.id}`,
        }))
      } catch (error) {
        console.error('Aktif görseller yüklenirken hata oluştu:', error)
      }
    },
    openBroadcastDialog() {
      this.broadcastError = ''
      this.broadcastSuccess = ''
      this.broadcastForm = defaultBroadcastForm()
      this.broadcastConfirmed = false
      this.sendMode = 'NOW'
      this.destination = 'GENERAL'
      this.destinations = []
      this.testUserId = ''
      this.testResult = ''
      this.broadcastDialog = true
      this.loadAudience()
    },
    closeBroadcastDialog() {
      if (this.broadcastSaving || this.testSaving) return
      this.broadcastDialog = false
      this.broadcastError = ''
      this.broadcastForm = defaultBroadcastForm()
    },
    async saveBroadcast() {
      if (this.broadcastSaving || this.testSaving || !this.broadcastConfirmed || !this.audience || this.audience.eligibleAudience <= 0) return
      if (!this.validateBroadcast()) return
      if (!this.broadcastForm.title || !this.broadcastForm.title.trim()) {
        this.broadcastError = 'Başlık zorunlu.'

        return
      }

      if (!this.broadcastForm.bodyText || !this.broadcastForm.bodyText.trim()) {
        this.broadcastError = 'Bildirim metni zorunlu.'

        return
      }

      this.broadcastSaving = true
      this.broadcastError = ''
      this.broadcastSuccess = ''
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL}admin/notifications/broadcasts`,
          {
            title: this.broadcastForm.title.trim(),
            bodyText: this.broadcastForm.bodyText.trim(),
            type: (this.broadcastForm.type || 'Broadcast').trim(),
            imageId: this.broadcastForm.imageId || null,
            scheduledAt: this.sendMode === 'SCHEDULED' ? this.broadcastForm.scheduledAt : '',
            secretData: this.broadcastForm.secretData?.trim() || '',
          },
          this.requestOptions,
        )
        this.broadcastSuccess = `#${response?.data?.data?.id || '-'} bildirim ${this.sendMode === 'SCHEDULED' ? 'zamanlandı' : 'kuyruğa alındı'}. Teslimat henüz doğrulanmadı.`
        this.broadcastDialog = false
        this.broadcastConfirmed = false
        await this.loadBroadcasts()
      } catch (error) {
        this.broadcastError = resolveApiErrorMessage(error, 'Bildirim kaydedilemedi.')
        console.error('Broadcast notification kaydedilirken hata oluştu:', error)
      } finally {
        this.broadcastSaving = false
      }
    },
    formatPayoutValue(value) {
      if (value === null || value === undefined || value === '') return '-'

      const numericValue = Number(value)
      if (Number.isNaN(numericValue)) return String(value)

      return new Intl.NumberFormat('tr-TR').format(numericValue)
    },
    formatPayoutDelta(oldValue, newValue) {
      const oldNumeric = Number(oldValue)
      const newNumeric = Number(newValue)

      if (Number.isNaN(oldNumeric) || Number.isNaN(newNumeric)) return '-'

      const diff = newNumeric - oldNumeric
      const sign = diff > 0 ? '+' : ''

      return `${sign}${new Intl.NumberFormat('tr-TR').format(diff)}`
    },
    panelEventLabel(type) {
      const normalized = String(type || '').trim().toLowerCase()

      if (normalized === 'admin-offer-payout-change') return 'Payout Değişimi'
      if (normalized === 'admin-offer-status-change') return 'Offer Durum Değişimi'
      if (normalized === 'admin-offer-created') return 'Yeni Offer'
      if (normalized === 'admin-offer-sync-completed') return 'Network Offer Sync'
      if (normalized === 'admin-campaign-url-health') return 'Kampanya URL Sağlığı'
      if (normalized === 'admin-whatsapp-support-message') return 'WhatsApp Destek'
      if (normalized === 'admin-referral-review') return 'Referral İncelemesi'
      if (normalized === 'admin-premium-state-change') return 'Premium Durum Değişimi'

      return type || 'Panel Bildirimi'
    },
    panelEventIcon(item) {
      const normalized = String(item?.type || '').trim().toLowerCase()

      if (normalized === 'admin-offer-payout-change') return icons.mdiCash
      if (normalized === 'admin-offer-status-change') return icons.mdiAlertCircleOutline
      if (normalized === 'admin-offer-created') return icons.mdiPlusCircleOutline
      if (normalized === 'admin-offer-sync-completed') return icons.mdiSync
      if (normalized === 'admin-campaign-url-health') return icons.mdiLinkVariant
      if (normalized === 'admin-whatsapp-support-message') return icons.mdiWhatsapp
      if (normalized === 'admin-referral-review') return icons.mdiAlertCircleOutline
      if (normalized === 'admin-premium-state-change') return icons.mdiCrownOutline

      return icons.mdiBellOutline
    },
    panelEventColor(item) {
      const normalized = String(item?.type || '').trim().toLowerCase()

      if (normalized === 'admin-offer-payout-change') {
        const oldValue = Number(item?.oldPayout)
        const newValue = Number(item?.newPayout)

        if (!Number.isNaN(oldValue) && !Number.isNaN(newValue) && newValue < oldValue) {
          return 'warning'
        }

        return 'success'
      }
      if (normalized === 'admin-offer-status-change') return 'error'
      if (normalized === 'admin-offer-created') return 'primary'
      if (normalized === 'admin-offer-sync-completed') return 'info'
      if (normalized === 'admin-campaign-url-health') {
        if (item?.healthStatus === 'HEALTHY' || item?.healthStatus === 'HEALTHY_REDIRECT') return 'success'
        if (item?.healthStatus === 'REVIEW_REDIRECT' || item?.healthStatus === 'TEMPORARY_ERROR' || item?.healthStatus === 'BLOCKED') return 'warning'

        return 'error'
      }
      if (normalized === 'admin-whatsapp-support-message') return 'success'
      if (normalized === 'admin-referral-review') return 'warning'
      if (normalized === 'admin-premium-state-change') {
        if (item.currentState === 'ACTIVE') return 'success'
        if (item.currentState === 'EXPIRED') return 'error'

        return 'warning'
      }

      return 'info'
    },
    buildPanelEventSubtitle(item) {
      if (!item) return '-'

      if (item.oldPayout !== null && item.oldPayout !== undefined && item.newPayout !== null && item.newPayout !== undefined) {
        return `Eski: ${this.formatPayoutValue(item.oldPayout)} • Yeni: ${this.formatPayoutValue(item.newPayout)} • Fark: ${this.formatPayoutDelta(item.oldPayout, item.newPayout)}`
      }

      return item.message || '-'
    },
    openCampaignUrlHealth(item) {
      this.$router.push({
        name: 'campaignUrlHealth',
        query: item?.campaignId ? { campaignId: String(item.campaignId) } : {},
      })
    },
    openRuleDialog(item = null) {
      this.ruleError = ''
      this.ruleSuccess = ''
      if (!item) {
        this.ruleForm = defaultRuleForm()
        this.editingRuleId = null
      } else {
        this.editingRuleId = item.id
        this.ruleForm = {
          code: item.code || '',
          name: item.name || '',
          description: item.description || '',
          triggerType: item.triggerType || 'LINK_IDLE_SHARE_REMINDER',
          titleTemplate: item.titleTemplate || '',
          messageTemplate: item.messageTemplate || '',
          type: item.type || 'Smart Push',
          secretDataTemplate: item.secretDataTemplate || '',
          active: item.active !== false,
          delayMinutes: item.delayMinutes || 120,
          cooldownMinutes: item.cooldownMinutes || 120,
          dedupWindowHours: item.dedupWindowHours || 24,
          peakHour: item.peakHour ?? 19,
          quietStart: item.quietStart || '23:00',
          quietEnd: item.quietEnd || '09:00',
          imageId: item.image?.id || null,
        }
      }
      this.ruleDialog = true
    },
    closeRuleDialog() {
      this.ruleDialog = false
      this.editingRuleId = null
      this.ruleError = ''
      this.ruleForm = defaultRuleForm()
    },
    async saveRule() {
      this.ruleSaving = true
      this.ruleError = ''
      this.ruleSuccess = ''
      try {
        if (this.editingRuleId) {
          await axios.put(
            `${process.env.VUE_APP_BASE_API_URL}admin/notifications/smart-rules/${this.editingRuleId}`,
            this.ruleForm,
            this.requestOptions,
          )
        } else {
          await axios.post(
            `${process.env.VUE_APP_BASE_API_URL}admin/notifications/smart-rules`,
            this.ruleForm,
            this.requestOptions,
          )
        }
        await this.loadRules()
        this.ruleSuccess = this.editingRuleId ? 'Akıllı push kuralı güncellendi.' : 'Akıllı push kuralı oluşturuldu.'
        this.closeRuleDialog()
      } catch (error) {
        this.ruleError = error?.response?.data?.message || 'Akıllı push kuralı kaydedilemedi.'
        console.error('Smart rule kaydedilirken hata oluştu:', error)
      } finally {
        this.ruleSaving = false
      }
    },
    resolveBroadcastCampaignName(item) {
      const type = String(item?.type || '').trim().toLowerCase()
      if (type === 'yeni marka') {
        return ''
      }

      return item?.campaignName || item?.linkUniqueId || ''
    },
    async uploadNotificationImage(target) {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/*'
      input.click()

      input.onchange = async () => {
        const file = input.files?.[0]
        if (!file) return

        this.imageUploadLoading = target
        try {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('folder', 'cdn.adverport.com/adverportaffiliate/notifications')
          const uploadResponse = await axios.post(
            `${process.env.VUE_APP_BASE_API_URL2}file/uploadFile`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${getAccessToken()}`,
                'Content-Type': 'multipart/form-data',
              },
            },
          )
          const imageId = uploadResponse?.data?.id || uploadResponse?.data?.data?.id
          if (imageId) {
            await axios.put(
              `${process.env.VUE_APP_BASE_API_URL}images/change-name/${imageId}?name=${encodeURIComponent(file.name)}`,
              {},
              this.requestOptions,
            )
            await this.loadActiveImages()
            if (target === 'rule') {
              this.ruleForm.imageId = imageId
            } else {
              this.broadcastForm.imageId = imageId
            }
          }
        } catch (error) {
          const message = error?.response?.data?.message || 'Görsel yüklenemedi.'
          if (target === 'rule') {
            this.ruleError = message
          } else {
            this.broadcastError = message
          }
          console.error('Notification görseli yüklenirken hata oluştu:', error)
        } finally {
          this.imageUploadLoading = ''
        }
      }
    },
    async toggleRuleActive(item, isActive) {
      if (!item || !item.id || this.ruleActionBusy) return
      this.ruleActionBusy = true
      this.ruleError = ''
      this.ruleSuccess = ''
      const targetRule = this.rules.find(rule => rule.id === item.id)
      const previousActive = item.active
      try {
        await axios.put(
          `${process.env.VUE_APP_BASE_API_URL}admin/notifications/smart-rules/${item.id}`,
          {
            ...item,
            imageId: item.image?.id || null,
            active: !!isActive,
          },
          this.requestOptions,
        )
        if (targetRule) {
          targetRule.active = !!isActive
        }
        this.ruleSuccess = `${item.name} ${isActive ? 'aktif' : 'pasif'} yapıldı.`
      } catch (error) {
        if (targetRule) {
          targetRule.active = previousActive
        }
        this.ruleError = error?.response?.data?.message || 'Kural durumu güncellenemedi.'
        console.error('Smart rule aktiflik güncellenirken hata oluştu:', error)
      } finally {
        this.ruleActionBusy = false
      }
    },
  },
}
</script>

<style scoped>
.notification-filters { display: grid; grid-template-columns: 2fr 1fr 40px; gap: 12px; margin-bottom: 12px; }
.notification-composer { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); gap: 24px; }
.notification-composer__form, .notification-composer__preview { min-width: 0; }
.notification-device-preview { padding: 16px; border: 1px solid #b9bec8; border-radius: 8px; overflow-wrap: anywhere; }
.notification-device-preview p { white-space: pre-wrap; margin: 8px 0 0; }
.notification-audience { display: grid; grid-template-columns: 1fr auto; gap: 8px; margin: 12px 0; }
.notification-audience dd { margin: 0; font-weight: 600; }
.notification-test-recipient { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px; align-items: center; }
@media (max-width: 760px) { .notification-composer { grid-template-columns: 1fr; } .notification-filters { grid-template-columns: 1fr; } }

.notification-broadcast__campaign {
  font-weight: 700;
  white-space: normal;
  word-break: break-word;
}

.notification-broadcast__campaign-name {
  white-space: normal;
  word-break: break-word;
}

.notification-feed__user,
.notification-feed__title {
  white-space: normal;
  word-break: break-word;
}

.notification-tabs {
  width: 100%;
}

.notification-all-toolbar {
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(300px, 1fr) auto;
  gap: 12px;
  align-items: center;
}

.notification-all-toolbar__count {
  justify-self: end;
}

.notification-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
}

.notification-broadcast__body {
  display: -webkit-box;
  overflow: hidden;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

@media (max-width: 960px) {
  .notification-header-actions,
  .notification-tabs {
    width: 100%;
  }

  .notification-header-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .notification-all-toolbar {
    grid-template-columns: 1fr;
  }

  .notification-all-toolbar__count {
    justify-self: start;
  }
}
</style>
